import { Area } from "react-easy-crop"

/**
 * Loads an image element from a URL (object URL or remote).
 * crossOrigin is set so remote images can be drawn to a canvas without
 * tainting it; local object URLs are unaffected.
 */
const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.addEventListener("load", () => resolve(image))
    image.addEventListener("error", error => reject(error))
    image.setAttribute("crossOrigin", "anonymous")
    image.src = url
  })

/**
 * Renders the user's selected crop region to a square canvas and returns it as
 * a JPEG File ready for upload. Output is downscaled to `outputSize` px so we
 * never store a full-resolution photo behind a small avatar.
 */
export const getCroppedImg = async (
  imageSrc: string,
  cropPixels: Area,
  outputSize = 512,
  fileName = "profile.jpg"
): Promise<File> => {
  const image = await createImage(imageSrc)
  const canvas = document.createElement("canvas")
  const ctx = canvas.getContext("2d")

  if (!ctx) {
    throw new Error("Could not get canvas context")
  }

  canvas.width = outputSize
  canvas.height = outputSize

  ctx.drawImage(
    image,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    outputSize,
    outputSize
  )

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (!blob) {
          reject(new Error("Canvas is empty"))
          return
        }
        // Build a clean filename whose ONLY dot is the .jpg extension.
        // The legacy upload backend detects the extension with
        // explode(".", name)[1], so any extra dots (or spaces) in the name
        // break extension detection and the picture silently fails to save.
        const baseName =
          fileName
            .replace(/\.[^/.]+$/, "") // drop the original extension
            .replace(/[^a-zA-Z0-9-_]+/g, "-") // dots/spaces/etc -> hyphen
            .replace(/-+/g, "-") // collapse repeats
            .replace(/^-|-$/g, "") || // trim leading/trailing hyphens
          "profile"
        resolve(new File([blob], `${baseName}.jpg`, { type: "image/jpeg" }))
      },
      "image/jpeg",
      0.9
    )
  })
}
